import { FC, lazy, Suspense, useEffect, useState } from 'react';
import type { ApexOptions } from 'apexcharts';
const ReactApexChart = lazy(() => import("react-apexcharts"));

interface DataItem {
  name?: string | null;
  revenue?: number | null;
}

interface AreaGraphProps {
  data?: DataItem[];
  isLoading?: boolean;
}

const CHART_COLOR = '#775DD0';

const AreaGrowthGraph: FC<AreaGraphProps> = ({
  data = [],
  isLoading = false,
}) => {
  const [chartConfig, setChartConfig] = useState<{
    series: ApexOptions['series'];
    options: ApexOptions;
  }>({
    series: [],
    options: {
      chart: {
        id: 'area-growth',
        type: 'area',
        height: 350,
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      colors: [CHART_COLOR],
      dataLabels: { enabled: false },
      stroke: { curve: 'smooth', width: 3 },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.2,
          stops: [0, 100],
        },
      },
      markers: { size: 5, hover: { size: 7 } },
      xaxis: {
        type: 'category',
        categories: [],
        labels: { style: { fontSize: '12px' } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        title: { text: 'Amount BHD' },
        labels: {
          formatter: (val: number) =>
            val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val.toString(),
        },
      },
      tooltip: {
        y: { formatter: (val: number) => `${val.toLocaleString()} BHD` },
      },
      grid: {
        borderColor: '#e7e7e7',
        strokeDashArray: 4,
      },
    },
  });

  useEffect(() => {
    if (!data || data.length === 0) {
      setChartConfig((prev) => ({
        ...prev,
        series: [],
        options: {
          ...prev.options,
          xaxis: { ...prev.options.xaxis, categories: [] },
        },
      }));
      return;
    }

    const categories = data.map((d) => d?.name ?? '');
    const seriesData = data.map((d) => d?.revenue ?? 0);

    setChartConfig({
      series: [{ name: 'Revenue', data: seriesData }],
      options: {
        ...chartConfig.options,
        xaxis: { ...chartConfig.options.xaxis, categories },
      },
    });
  }, [data]);

  return (
    <div id="area-growth-chart" className="w-full">
      {isLoading ? (
        <div className="flex justify-center items-center h-[350px]">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#775DD0] border-t-transparent"></div>
        </div>
      ) : data && data.length > 0 ? (
        <Suspense
          fallback={<div className="h-48 flex items-center justify-center">Loading chart…</div>}
        >
          <ReactApexChart
            key={JSON.stringify(chartConfig.series)} // 👈 Forces rerender when data changes
            options={chartConfig.options}
            series={chartConfig.series}
            type="area"
            height={350}
          />
        </Suspense>
      ) : (
        <div className="flex justify-center items-center h-[350px] border rounded-lg bg-gray-50 text-gray-500">
          No data available
        </div>
      )}
    </div>
  );
};

export default AreaGrowthGraph;
